import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { CustomElement, OverrideCustomElementConfig } from "../../components/customElement.decorator";
import { TypedHtmlElements } from "../../components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "../../components/typedWebComponentOptions.decorator";
import { WebComponent } from "../../components/webComponent";
import { Display, toggleClass, toggleVisible } from "../../tools/css.tools";
import { HOURGLASS_SERVICE, HourglassService, HourglassServiceOptions } from "../hourglass.service";

/** zIndex style of darkDiv hourglass */
export const DARK_HOUR_GLASS_Z_INDEX = 10000;

/** ZIndex used to display a dialog over hourGlass */
export const DARK_HOUR_GLASS_DIALOG_OVER_Z_INDEX = DARK_HOUR_GLASS_Z_INDEX + 1;

/** Define hourglass class name to use when we want to display loader and text inline. */
const HOURGLASS_INLINE_CLASS_NAME = "hourglass-loader-text-inline";

interface HourglassServiceProcessOptions<CustomData> extends HourglassServiceOptions<CustomData> {

    /**
     * DarkDiv of process, defined only if parent is given in options
     * Used to display darkDiv in subElements of process
     */
    darkDiv?: DarkDivHourGlass;

}

/**
 * Hourglass service that displays a dark div on the screen
 */
@Injectable(HOURGLASS_SERVICE)
export class DarkDivHourGlassService<CustomData = void> implements HourglassService<CustomData> {

    /** Next id of hourGlass process */
    protected _nextId = 1;

    /** Displayed processes in application */
    protected _displayedProcesses = new Map<number, HourglassServiceProcessOptions<CustomData>>();// Map keeps order

    /** Main darkDiv component, displayed in full page only */
    protected _darkDiv?: DarkDivHourGlass;

    /**
     * Default event handler used to disable an event
     *
     */
    private _eventNullifier: EventListenerObject = {
        handleEvent: e => e.preventDefault()
    };

    /** Show loading */
    public show(options?: HourglassServiceOptions<CustomData>): number {
        const id = this._nextId++;
        if (!options?.doNotDisableEvents) {
            this._disableEvents();
        }
        if (options?.showOnlyIfLongerThanMs) {
            this._displayedProcesses.set(id, { ...options ?? {} });
            setTimeout(() => {
                this._refresh(id);
            }, options.showOnlyIfLongerThanMs);
        } else {
            this.update(id, options);
        }
        return id;
    }

    /**
     * Update a loading, set a process
     * Update can be:
     * - Progress counter
     * - Data
     * - Message displayed
     */
    public update(id: number, options?: HourglassServiceOptions<CustomData>): void {
        this._displayedProcesses.set(id, { ...options ?? {} });
        this._refresh(id);
    }

    /** 
     * Hide a loading processes
     * DarkDiv can still be visible if at least one process remain
     */
    public hide(id: number): void {

        // Retrieve process, check if process has self darkDiv defined
        const process = this._displayedProcesses.get(id);

        if (process) { // MGS : check process in case it has already been deleted
            const processDarkDiv = process?.darkDiv;
            if (processDarkDiv) {
                // If darkDiv defined, remove it
                processDarkDiv.remove();
                process.darkDiv = undefined;
            }

            // Delete process from map
            this._displayedProcesses.delete(id);

            // MGS : Check if there stills blocking process and global process
            let still_blocking_process = false, still_global_process = false;
            for (const displayedProcess of this._displayedProcesses.values()) {
                still_blocking_process ||= !displayedProcess.doNotDisableEvents;
                still_global_process ||= !displayedProcess.parent;

                if (still_blocking_process && still_global_process) { break; }
            }

            // MGS : if no more blocking process, enable events
            if (!still_blocking_process) {
                this._enableEvents();
            }

            // MGS : if no more process without parent : remove darkDiv if exists
            if (!still_global_process && this._darkDiv) {
                this._darkDiv.remove();
                this._darkDiv = undefined;
            }

            // Actualize display
            this._refresh(id);
        }
    }

    /** Reset all loading processes */
    public reset(): void {

        // Foreach process, remove self darkDiv if defined
        for (const process of this._displayedProcesses.values()) {
            const processDarkDiv = process?.darkDiv;
            if (processDarkDiv) {
                processDarkDiv.remove();
                process.darkDiv = undefined;
            }
        }

        // Clear all process
        this._displayedProcesses.clear();

        // Enable events
        this._enableEvents();

        // Remove globalDarkDiv if exists
        if (this._darkDiv) {
            this._darkDiv.remove();
            this._darkDiv = undefined;
        }
    }

    /**
     * Displays the dark div according to currently displayed processes
     */
    protected _refresh(id?: number): void {

        if (this._displayedProcesses.size === 0) { return; }

        // Get process remaining, if id is given, get it, else retrieve first process subscribe
        const processRemaining = id ? this._displayedProcesses.get(id) : [...this._displayedProcesses][0][1];
        if (processRemaining) {

            // Build darkDivHourGlass options, from process options
            const componentOptions: DarkDivHourGlassOptions = {
                label: processRemaining?.message ?? "",
                progress: processRemaining?.progress ?? 0,
                displayProgress: Boolean(processRemaining?.displayProgress)
            };

            // If process has parent, create and add darkDiv to it if doesn't have yet
            const processParent = processRemaining?.parent;
            if (processParent) {
                if (!processRemaining.darkDiv) {
                    processRemaining.darkDiv = new DarkDivHourGlass(componentOptions);
                    processParent.append(processRemaining.darkDiv);
                    toggleClass(processRemaining.darkDiv, Boolean(processRemaining.inlineDarkDiv), HOURGLASS_INLINE_CLASS_NAME);
                }
            }

            // if darkDiv doesn't exist and there is more than 1 blocking process, create it
            else if (!this._darkDiv) {
                this._darkDiv = new DarkDivHourGlass(componentOptions);
                document.body.append(this._darkDiv);
            } else {
                // Refresh darkDiv options from process value
                this._darkDiv.options.setAndRefreshIfChanged(componentOptions);
            }
        }
    }

    /**
     * Disable events when the dark div is displayed
     */
    private _disableEvents(): void {
        window.addEventListener("wheel", this._eventNullifier, { passive: false });
    }
    /**
     * Enable the previous disabled events
     */
    private _enableEvents(): void {
        window.removeEventListener("wheel", this._eventNullifier);
    }

    /** Show loading, compute and hide loading */
    public async encapsulate<T>(compute: (hourGlassId: number) => T | Promise<T>, options?: HourglassServiceOptions<CustomData> & { doNotWait?: boolean }): Promise<T> {
        const hourGlassId = this.show(options);
        if (!options?.doNotWait) {
            // wait for hour glass to be displayed
            // TODO find a better way to wait (note that using an observer will not work)
            await new Promise((resolve, reject) => {
                try {
                    setTimeout(() => {
                        resolve(undefined);
                    }, 50);
                } catch (e) {
                    resolve(undefined);
                }
            });
        }
        try {
            const result = await compute(hourGlassId);
            return result;
        } finally {
            this.hide(hourGlassId);
        }
    }

}

/** Parameters for DarkDivHourGlass component */
export interface DarkDivHourGlassOptions {

    /** Progress of loader, from 0 to 100% */
    progress: number;

    /** Label of loader */
    label: string;

    /** Display progress of loading */
    displayProgress: boolean;
}

/** Parameters for DarkDivHourGlass */
export const defaultDarkDivHourGlassOptions: DarkDivHourGlassOptions = {

    /** Default number of columns in picker */
    progress: 0,

    /** Enable recent colors section */
    label: "",

    /** Display progress of loading */
    displayProgress: false
};

@CustomElement({
    name: "dark-div-hourglass",
    style: require("./darkDiv.scss"),
    template: require("./darkDiv.html")
})
export class DarkDivHourGlass extends WebComponent {

    /** Components */
    @TypedHtmlElements()
    public readonly components!: {
        darkDivLoader: HTMLDivElement,
        darkDivText: HTMLDivElement,
        darkProgressText: HTMLDivElement
    };

    @TypedWebComponentOptions(defaultDarkDivHourGlassOptions)
    public readonly options!: WebComponentOptions<DarkDivHourGlassOptions>;

    constructor(options?: Partial<DarkDivHourGlassOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** Refresh darkDiv progress */
    public override refresh(): void {
        this._refreshLabel();
        this._refreshProgress();
    }

    /** Refresh label of darkDiv */
    private _refreshLabel(): void {
        this.components.darkDivText.innerText = this.options.label;
    }

    /** Refresh progress of darkDiv */
    private _refreshProgress(): void {
        toggleVisible(this.components.darkProgressText, this.options.displayProgress, Display.FLEX);
        this.components.darkProgressText.innerHTML = this.options.progress + " %";
    }

}